import { useState, useEffect, useCallback } from 'react';
import { useLang } from '../contexts/LangContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Search, X, Upload, ImageIcon, Loader2 } from 'lucide-react';
import TablePagination from '../components/TablePagination';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PRODUCT_TYPES = ['service', 'physique', 'digital', 'package'];
const FEE_TYPES = ['nominal', 'percentage'];

const imgSrc = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${BACKEND_URL}${url}`;
};

export default function ProductPage() {
  const { t } = useLang();
  const [products, setProducts] = useState([]);
  const [stores, setStores] = useState([]);
  const [categories, setCategories] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 20, total_pages: 1 });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    name: '', barcode: '', product_type: 'physique', stock: 0,
    commission_items: [], selling_price: 0, image_url: '', store_id: '', package_items: [], category_id: ''
  });

  const loadData = useCallback(async (page = 1) => {
    try {
      const [prodRes, storeRes, catRes] = await Promise.all([
        api.listProducts(page, 20),
        api.listStores(1, 1000),
        api.listAllCategories()
      ]);
      setProducts(prodRes.data.data);
      setAllProducts(prodRes.data.data);
      setPagination({ total: prodRes.data.total, page: prodRes.data.page, limit: prodRes.data.limit, total_pages: prodRes.data.total_pages });
      setStores(storeRes.data.data || storeRes.data);
      setCategories(catRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...form, selling_price: Number(form.selling_price), stock: Number(form.stock) };
      if (editing) { await api.updateProduct(editing.id, data); }
      else { await api.createProduct(data); }
      toast.success(t('success'));
      closeDialog();
      loadData(pagination.page);
    } catch (err) { toast.error(err?.response?.data?.detail || t('error')); }
  };

  const handleEdit = (prod) => {
    setEditing(prod);
    setForm({
      name: prod.name, barcode: prod.barcode || '', product_type: prod.product_type,
      stock: prod.stock || 0, commission_items: prod.commission_items || [],
      selling_price: prod.selling_price, image_url: prod.image_url || '',
      store_id: prod.store_id || '', package_items: prod.package_items || [],
      category_id: prod.category_id || ''
    });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false); setEditing(null);
    setForm({ name: '', barcode: '', product_type: 'physique', stock: 0, commission_items: [], selling_price: 0, image_url: '', store_id: '', package_items: [], category_id: '' });
  };

  const handleDelete = async () => {
    try { await api.deleteProduct(deleteId); toast.success(t('success')); setDeleteId(null); loadData(pagination.page); }
    catch (err) { toast.error(t('error')); }
  };

  const addCommission = () => {
    setForm({ ...form, commission_items: [...form.commission_items, { name: '', fee: 0, fee_type: 'nominal' }] });
  };
  const updateCommission = (idx, field, value) => {
    const items = [...form.commission_items];
    items[idx] = { ...items[idx], [field]: field === 'fee' ? Number(value) : value };
    setForm({ ...form, commission_items: items });
  };
  const removeCommission = (idx) => {
    setForm({ ...form, commission_items: form.commission_items.filter((_, i) => i !== idx) });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Hanya file gambar yang diizinkan'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Ukuran file maksimal 5MB'); return; }
    setUploading(true);
    try {
      const res = await api.uploadImage(file);
      setForm(prev => ({ ...prev, image_url: res.data.url }));
      toast.success('Gambar berhasil diupload');
    } catch (err) { toast.error('Gagal upload gambar'); }
    finally { setUploading(false); }
  };

  const togglePackageItem = (productId) => {
    const items = form.package_items.includes(productId) ? form.package_items.filter(id => id !== productId) : [...form.package_items, productId];
    setForm({ ...form, package_items: items });
  };

  const typeBadge = (type) => {
    const colors = { service: 'bg-blue-100 text-blue-700 border-blue-200', physique: 'bg-green-100 text-green-700 border-green-200', digital: 'bg-purple-100 text-purple-700 border-purple-200', package: 'bg-amber-100 text-amber-700 border-amber-200' };
    return <Badge className={`${colors[type] || ''} text-xs border`}>{t(type)}</Badge>;
  };

  const formatCurrency = (val) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(val || 0);

  const getCategoryName = (catId) => categories.find(c => c.id === catId)?.name || '';

  const filtered = products.filter(p =>
    p.name?.toLowerCase().includes(search.toLowerCase()) || p.barcode?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="product-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#202020]" style={{ fontFamily: 'Manrope' }}>{t('products')}</h1>
          <p className="text-sm text-[#64584F]">{pagination.total} {t('products').toLowerCase()}</p>
        </div>
        <Button onClick={() => { closeDialog(); setDialogOpen(true); }} className="bg-[#EB7B44] hover:bg-[#D96A35] text-white font-medium" data-testid="add-product-btn">
          <Plus size={16} className="mr-2" /> {t('add')} {t('products')}
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A6A2A0]" />
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder={`${t('search')}...`} className="pl-9 bg-[#F6F5F5] border-transparent focus:bg-white focus:border-[#EB7B44]" data-testid="product-search-input" />
      </div>

      <Card className="bg-white border-[#CACACC] rounded-xl shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F6F5F5] border-b border-[#CACACC]">
              <TableHead className="text-xs font-bold text-[#64584F] uppercase tracking-wider">{t('name')}</TableHead>
              <TableHead className="text-xs font-bold text-[#64584F] uppercase tracking-wider hidden lg:table-cell">Kategori</TableHead>
              <TableHead className="text-xs font-bold text-[#64584F] uppercase tracking-wider hidden md:table-cell">{t('barcode')}</TableHead>
              <TableHead className="text-xs font-bold text-[#64584F] uppercase tracking-wider">{t('type')}</TableHead>
              <TableHead className="text-xs font-bold text-[#64584F] uppercase tracking-wider hidden sm:table-cell">{t('stock')}</TableHead>
              <TableHead className="text-xs font-bold text-[#64584F] uppercase tracking-wider">{t('price')}</TableHead>
              <TableHead className="text-xs font-bold text-[#64584F] uppercase tracking-wider text-right">{t('actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} className="text-center py-12 text-[#A6A2A0]">{t('loading')}</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-12 text-[#A6A2A0]">{t('no_data')}</TableCell></TableRow>
            ) : filtered.map(prod => (
              <TableRow key={prod.id} className="border-b border-[#DFDEDE] hover:bg-[#F6F5F5]/50 transition-colors" data-testid={`product-row-${prod.id}`}>
                <TableCell className="font-medium text-[#202020]">
                  <div className="flex items-center gap-3">
                    {prod.image_url ? (
                      <img src={imgSrc(prod.image_url)} alt={prod.name} className="w-9 h-9 rounded-lg object-cover border border-[#DFDEDE] flex-shrink-0" />
                    ) : (
                      <div className="w-9 h-9 rounded-lg bg-[#F6F5F5] border border-[#DFDEDE] flex items-center justify-center flex-shrink-0">
                        <ImageIcon size={14} className="text-[#A6A2A0]" />
                      </div>
                    )}
                    <span>{prod.name}</span>
                  </div>
                </TableCell>
                <TableCell className="text-[#64584F] text-sm hidden lg:table-cell">
                  {getCategoryName(prod.category_id) || <span className="text-[#CACACC]">-</span>}
                </TableCell>
                <TableCell className="text-[#64584F] hidden md:table-cell">{prod.barcode || '-'}</TableCell>
                <TableCell>{typeBadge(prod.product_type)}</TableCell>
                <TableCell className="text-[#64584F] hidden sm:table-cell">{prod.stock === -1 ? '\u221E' : prod.stock}</TableCell>
                <TableCell className="text-[#EB7B44] font-semibold">{formatCurrency(prod.selling_price)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(prod)} data-testid={`product-edit-btn-${prod.id}`}>
                      <Pencil size={15} className="text-[#64584F]" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setDeleteId(prod.id)} data-testid={`product-delete-btn-${prod.id}`}>
                      <Trash2 size={15} className="text-red-500" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination page={pagination.page} totalPages={pagination.total_pages} total={pagination.total} limit={pagination.limit} onPageChange={(p) => loadData(p)} />
      </Card>

      {/* Product Dialog */}
      <Dialog open={dialogOpen} onOpenChange={closeDialog}>
        <DialogContent className="bg-white border-[#CACACC] rounded-xl max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#202020]" style={{ fontFamily: 'Manrope' }}>
              {editing ? t('edit') : t('add')} {t('products')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#202020]">{t('name')}</Label>
                <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required className="border-[#CACACC]" data-testid="product-name-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-[#202020]">{t('barcode')} (optional)</Label>
                <Input value={form.barcode} onChange={e => setForm({...form, barcode: e.target.value})} className="border-[#CACACC]" data-testid="product-barcode-input" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#202020]">{t('product_type')}</Label>
                <Select value={form.product_type} onValueChange={val => setForm({...form, product_type: val})}>
                  <SelectTrigger className="border-[#CACACC]" data-testid="product-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{PRODUCT_TYPES.map(pt => <SelectItem key={pt} value={pt}>{t(pt)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {form.product_type === 'physique' && (
                <div className="space-y-2">
                  <Label className="text-[#202020]">{t('stock')}</Label>
                  <Input type="number" value={form.stock} onChange={e => setForm({...form, stock: e.target.value})} className="border-[#CACACC]" data-testid="product-stock-input" />
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#202020]">{t('selling_price')}</Label>
                <Input type="number" value={form.selling_price} onChange={e => setForm({...form, selling_price: e.target.value})} required className="border-[#CACACC]" data-testid="product-price-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-[#202020]">Kategori</Label>
                <Select value={form.category_id || "__none__"} onValueChange={val => setForm({...form, category_id: val === "__none__" ? "" : val})}>
                  <SelectTrigger className="border-[#CACACC]" data-testid="product-category-select"><SelectValue placeholder="Tanpa Kategori" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Tanpa Kategori</SelectItem>
                    {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#202020]">{t('store')}</Label>
                <Select value={form.store_id || "__all__"} onValueChange={val => setForm({...form, store_id: val === "__all__" ? "" : val})}>
                  <SelectTrigger className="border-[#CACACC]" data-testid="product-store-select"><SelectValue placeholder="Semua Toko" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Semua Toko</SelectItem>
                    {stores.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Image Upload */}
            <div className="space-y-2">
              <Label className="text-[#202020] font-semibold">{t('image')}</Label>
              <div className="flex items-start gap-4">
                {form.image_url ? (
                  <div className="relative w-24 h-24 rounded-lg overflow-hidden border border-[#CACACC] bg-[#F6F5F5] flex-shrink-0">
                    <img src={imgSrc(form.image_url)} alt="Product" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => setForm({...form, image_url: ''})} className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center"><X size={12} /></button>
                  </div>
                ) : (
                  <div className="w-24 h-24 rounded-lg border-2 border-dashed border-[#CACACC] bg-[#F6F5F5] flex items-center justify-center flex-shrink-0"><ImageIcon size={24} className="text-[#A6A2A0]" /></div>
                )}
                <div className="flex-1 space-y-2">
                  <label className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-[#CACACC] bg-white cursor-pointer hover:bg-[#F6F5F5] transition-colors ${uploading ? 'opacity-60 pointer-events-none' : ''}`} data-testid="product-image-upload-btn">
                    {uploading ? <Loader2 size={16} className="text-[#EB7B44] animate-spin" /> : <Upload size={16} className="text-[#64584F]" />}
                    <span className="text-sm text-[#64584F] font-medium">{uploading ? 'Uploading...' : 'Upload Gambar'}</span>
                    <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" data-testid="product-image-file-input" />
                  </label>
                  <p className="text-xs text-[#A6A2A0]">JPG, PNG, WebP. Maks 5MB</p>
                  <Input value={form.image_url} onChange={e => setForm({...form, image_url: e.target.value})} placeholder="Atau masukkan URL gambar" className="border-[#CACACC] text-sm h-9" data-testid="product-image-url-input" />
                </div>
              </div>
            </div>

            {/* Commission Items */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-[#202020] font-semibold">{t('commission_items')}</Label>
                <Button type="button" variant="outline" size="sm" onClick={addCommission} className="border-[#CACACC] text-[#64584F]" data-testid="add-commission-btn">
                  <Plus size={14} className="mr-1" /> {t('add')}
                </Button>
              </div>
              {form.commission_items.map((ci, idx) => (
                <div key={idx} className="flex items-end gap-2 p-3 bg-[#F6F5F5] rounded-lg">
                  <div className="flex-1 space-y-1">
                    <Label className="text-xs text-[#64584F]">{t('name')}</Label>
                    <Input value={ci.name} onChange={e => updateCommission(idx, 'name', e.target.value)} className="h-9 border-[#CACACC] bg-white" data-testid={`commission-name-${idx}`} />
                  </div>
                  <div className="w-24 space-y-1">
                    <Label className="text-xs text-[#64584F]">{t('fee')}</Label>
                    <Input type="number" value={ci.fee} onChange={e => updateCommission(idx, 'fee', e.target.value)} className="h-9 border-[#CACACC] bg-white" data-testid={`commission-fee-${idx}`} />
                  </div>
                  <div className="w-28 space-y-1">
                    <Label className="text-xs text-[#64584F]">{t('fee_type')}</Label>
                    <Select value={ci.fee_type} onValueChange={val => updateCommission(idx, 'fee_type', val)}>
                      <SelectTrigger className="h-9 border-[#CACACC] bg-white"><SelectValue /></SelectTrigger>
                      <SelectContent>{FEE_TYPES.map(ft => <SelectItem key={ft} value={ft}>{t(ft)}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <Button type="button" variant="ghost" size="sm" onClick={() => removeCommission(idx)} className="text-red-500 h-9"><X size={14} /></Button>
                </div>
              ))}
            </div>

            {/* Package Items */}
            {form.product_type === 'package' && (
              <div className="space-y-3">
                <Label className="text-[#202020] font-semibold">{t('package_items')}</Label>
                <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto p-2 bg-[#F6F5F5] rounded-lg">
                  {allProducts.filter(p => p.id !== editing?.id).map(p => (
                    <label key={p.id} className="flex items-center gap-2 p-2 rounded bg-white cursor-pointer hover:bg-[#F6F5F5]">
                      <input type="checkbox" checked={form.package_items.includes(p.id)} onChange={() => togglePackageItem(p.id)} className="accent-[#EB7B44]" />
                      <span className="text-sm text-[#202020] truncate">{p.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={closeDialog} className="border-[#CACACC]">{t('cancel')}</Button>
              <Button type="submit" className="bg-[#EB7B44] hover:bg-[#D96A35] text-white" data-testid="product-save-btn">{t('save')}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <DialogContent className="bg-white border-[#CACACC] rounded-xl max-w-sm">
          <DialogHeader><DialogTitle className="text-[#202020]">{t('confirm_delete')}</DialogTitle></DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteId(null)} className="border-[#CACACC]">{t('cancel')}</Button>
            <Button onClick={handleDelete} className="bg-red-500 hover:bg-red-600 text-white" data-testid="confirm-delete-product-btn">{t('delete')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
